import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { useLoadingStore } from '@/stores/loading'
import router from '@/router'

export class InitService {
  constructor() { }

  public async post<T = any>(url: string, data?: any, cache: boolean = false): Promise<T> {
    if (cache && import.meta.env.VITE_ATENA_INNER_CACHE) {
      const lastSavedState = localStorage.getItem(url)
      if (lastSavedState != null) {
        return await Promise.resolve({ data: JSON.parse(lastSavedState) } as T)
      }
    }

    return await this.http.post(url, data)
  }

  public async get<T = any>(url: string, cache: boolean = false, config?: AxiosRequestConfig | undefined): Promise<T> {
    if (cache && import.meta.env.VITE_ATENA_INNER_CACHE == 'true') {
      const lastSavedState = localStorage.getItem(url)
      if (lastSavedState != null) {
        return await Promise.resolve({ data: JSON.parse(lastSavedState) } as T)
      }
    }

    return await this.http.get(url, config);
  }

  public get http() {
    return this.getAxiosInstance()
  }

  private getAxiosInstance(): AxiosInstance {
    const isDevelopment = import.meta.env.DEV || import.meta.env.VITE_IS_DEVELOPMENT === 'true'
    let url = isDevelopment
      ? `${window.location.protocol}//${window.location.hostname}:5000/api`
      : import.meta.env.VITE_ROOT_API_EXT || import.meta.env.VITE_ROOT_API
    if (url == null) {
      throw new Error('No url configuration')
    }

    if (window.location.protocol == 'https:' && !url.includes('https://')) {
      url = url.replace('http', 'https')
    }

    let axiosInstance: AxiosInstance

    if (window.localStorage.token != null) {
      axiosInstance = axios.create({
        baseURL: url,
        headers: {
          Authorization: `Bearer ${window.localStorage.token}`
        }
      })
    } else {
      axiosInstance = axios.create({
        baseURL: url
      })
    }

    axiosInstance.interceptors.request.use(this.handleRequest)
    axiosInstance.interceptors.response.use(this.handleResponse, this.handleError)

    return axiosInstance
  }

  private handleRequest(value: InternalAxiosRequestConfig<any>): InternalAxiosRequestConfig<any> {
    const loadingStore = useLoadingStore()
    loadingStore.openRequest()
    return value
  }

  private handleResponse(response: AxiosResponse): AxiosResponse {
    const loadingStore = useLoadingStore()
    loadingStore.closeRequest()

    if (import.meta.env.VITE_ATENA_INNER_CACHE == 'true' && response.config.responseType !== 'blob') {
      localStorage.setItem(response.config.url || '', JSON.stringify(response.data))
    }

    return response
  }

  private handleError(error: any) {
    const loadingStore = useLoadingStore()
    loadingStore.closeRequest()

    window.console.log(error)
    if (error.response?.status == 401) {
      router.push('/login')
      console.log(error)
      return Promise.reject(error)
    }

    if (import.meta.env.VITE_ATENA_USE_CACHE_ON_ERROR == 'true') {
      const url = error.config?.url || ''
      const lastSavedState = localStorage.getItem(url)
      if (lastSavedState != null) {
        console.log(`Failed to fetch ${url}, using the last saved state.`)
        return { data: JSON.parse(lastSavedState) }
      }
    }

    return Promise.reject(error)
  }
}
